import styled, { css } from 'styled-components';
import type { DefaultTheme } from 'styled-components';

import media from 'apps/auth/theme/media';

type ThemeProps = {
    theme: DefaultTheme;
};

type PlasticProps = {
    $isPlastic?: boolean;
};

type StyledPlasticProps = PlasticProps & ThemeProps;

type SmallCardProps = {
    $isPurple?: boolean;
    $isLinked?: boolean;
};

type BigCardProps = {
    $isPlastic?: boolean;
    $isBlocked?: boolean;
};

const BottomCutoutWhite =
    'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz48c3ZnIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB3aWR0aD0iNDhweCIgaGVpZ2h0PSIxNnB4IiB2aWV3Qm94PSIwIDAgNDggMTYiPjxyZWN0IGZpbGw9IiNmZmYiIHdpZHRoPSI0OCIgaGVpZ2h0PSIxNiIvPjxwYXRoIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzE0MTQxNCIgc3Ryb2tlLXdpZHRoPSIyIiBkPSJNNDgsMTVoLTcuMTc1Yy0yLjU4MywwLTQuODU5LTEuNzA1LTYuNDE4LTMuNzY1QzMyLjUzOSw4Ljc2OSwyOS4yNyw2LDI0LDYgcy04LjUzOSwyLjc2OS0xMC40MDYsNS4yMzVDMTIuMDM1LDEzLjI5NSw5Ljc1OCwxNSw3LjE3NSwxNUgwIi8+PC9zdmc+';

const BottomCutoutPurple =
    'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz48c3ZnIHZlcnNpb249IjEuMSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2aWV3Qm94PSIwIDAgNDggMTYiIHdpZHRoPSI0OCIgaGVpZ2h0PSIxNiI+PHJlY3QgZmlsbD0iI2ZmZiIgd2lkdGg9IjQ4IiBoZWlnaHQ9IjE2Ii8+PHBhdGggZD0iTTQ4IDBWMTZINDAuODI0NkMzNy43OTUgMTYgMzUuMjU3OSAxNC4wMTcyIDMzLjYwOSAxMS44MzlDMzEuODY1NiA5LjUzNjEgMjguODYyIDcgMjQgN0MxOS4xMzggNyAxNi4xMzQ0IDkuNTM2MSAxNC4zOTEgMTEuODM5QzEyLjc0MjEgMTQuMDE3MiAxMC4yMDUgMTYgNy4xNzU0NCAxNkgwVjBINDhaIiBmaWxsPSIjNzYzM0ZGIi8+PC9zdmc+';

const CommonSelection = css<StyledPlasticProps>`
    &::selection {
        background: ${(props) => (props.$isPlastic ? props.theme.white_24_tr_base : null)} !important;
    }
`;

const getCardInfoColor = (props: StyledPlasticProps) => {
    if (props.$isPlastic) {
        return props.theme.white_base;
    }
    return props.theme.grey_72_lc;
};

const CommonInfoStyle = css<StyledPlasticProps>`
    user-select: all;
    color: ${(props) => getCardInfoColor(props)};
`;

const getCardBackground = (props: BigCardProps & ThemeProps) => {
    if (props.$isPlastic && !props.$isBlocked) {
        return props.theme.purple_100;
    }
    return props.theme.white;
};

const getCardBorder = (props: BigCardProps & ThemeProps) => {
    if (props.$isPlastic) {
        return props.theme.purple_100;
    }
    return props.theme.black;
};

export const CardSmallContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== '$isPurple' && prop !== '$isLinked',
})<SmallCardProps>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-radius: 12px;
    border-width: 2px;
    border-style: solid;
    border-color: ${(props) => (props.$isPurple ? props.theme.purple_100 : props.theme.black)};
    min-height: 30px;
    padding: 26px 24px;
    position: relative;
    background: ${(props) => (props.$isPurple ? props.theme.purple_100 : props.theme.white)};
    box-sizing: border-box;

    ${media.desktop1920} {
        min-height: 50px;
        padding: 42px 32px;
        border-radius: 16px;
    }

    &:before {
        display: ${(props) => (props.$isLinked ? 'inline-block' : 'none')};
        content: '';
        position: absolute;
        left: 50%;
        margin-left: -24px;
        bottom: -2px;
        width: 48px;
        height: 16px;
        background-image: url('${(props) => (props.$isPurple ? BottomCutoutPurple : BottomCutoutWhite)}');
    }
`;

export const CardBigContainer = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== '$isPlastic' && prop !== '$isBlocked',
})<BigCardProps>`
    min-height: 192px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    border-radius: 12px;
    border-width: 2px;
    border-style: solid;
    border-color: ${(props) => getCardBorder(props)};
    position: relative;
    background: ${(props) => getCardBackground(props)};
    box-sizing: border-box;

    ${media.mobile481} {
        min-height: 264px;
    }

    ${media.desktop1920} {
        padding: 40px 32px 32px 32px;
        min-height: 312px;
        border-radius: 16px;
    }
`;

export const DigitsRow = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== '$isPlastic',
})<StyledPlasticProps>`
    display: flex;
    justify-content: space-between;
    user-select: all;
    color: ${(props) => (props.$isPlastic ? props.theme.white_base : props.theme.black)};
`;
export const DigitsGroup = styled.span.withConfig({
    shouldForwardProp: (prop) => prop !== '$isPlastic',
})<StyledPlasticProps>`
    letter-spacing: 0.1em;
    ${CommonSelection};

    ${media.mobile481} {
        letter-spacing: 0.4em;
    }

    ${media.tablet720} {
        letter-spacing: 0.6em;
    }
`;

export const CardholderName = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== '$isPlastic',
})<StyledPlasticProps>`
    user-select: all;
    ${CommonSelection};
`;

export const ExpDateContainer = styled.div``;
export const ExpDate = styled.span.withConfig({
    shouldForwardProp: (prop) => prop !== '$isPlastic',
})<StyledPlasticProps>`
    ${CommonInfoStyle};
    ${CommonSelection};
`;

export const CvvContainer = styled.div`
    margin-left: 16px;

    ${media.desktop1920} {
        margin-left: 32px;
    }
`;
export const Cvv = styled.span.withConfig({
    shouldForwardProp: (prop) => prop !== '$isPlastic',
})<StyledPlasticProps>`
    ${CommonInfoStyle};
    ${CommonSelection};
`;
